import * as React from 'react';
import { getAccessToken, getIdentityCached, setIdentityCached, type Identity } from '../../api/tokenStorage';
import { apiFetch } from '../../api/httpClient';
import { APPS_REGISTRY, type AppRegistryEntry } from '../appsRegistry';
import {
    getInstalledApps,
    isAppInstalledIn,
    type TenantAppEntry,
} from './tenantAppsUtils';
import { resolveStartupApp } from './startupAppResolver';

export type TenantAppsContextValue = {
    installedAppIds: string[];
    isLoaded: boolean;
    isAppInstalled: (appId: string) => boolean;
    getInstalledApps: () => AppRegistryEntry[];
    /** Recommended startup app resolved from lastUsedAppId → defaultAppId → first installed → fbr-smart. */
    defaultStartupAppId: string | undefined;
    /** Call when user explicitly opens an app. Persists to localStorage + background API. */
    persistLastUsedApp: (appId: string) => void;
    reload: () => Promise<void>;
};

export const TenantAppsContext = React.createContext<TenantAppsContextValue | null>(null);

export function TenantAppsProvider({ children }: { children: React.ReactNode }) {
    const [installedAppIds, setInstalledAppIds] = React.useState<string[]>(
        () => getIdentityCached()?.apps ?? []
    );
    const [identity, setIdentity] = React.useState<Identity | null>(() => getIdentityCached());
    const [isLoaded, setIsLoaded] = React.useState(false);

    // Fetches authoritative data from the API.
    // No early-return on missing token: apiFetch will attempt a refresh-cookie dance,
    // which correctly loads apps when only a refresh cookie exists, and clears auth cache
    // (setting getAccessToken() to null) when both token and cookie are expired.
    const reload = React.useCallback(async () => {
        try {
            const res = await apiFetch('/api/tenant/apps');
            if (res.ok) {
                const data: TenantAppEntry[] = await res.json();
                setInstalledAppIds(data.filter(a => a.isInstalled).map(a => a.appId));
            } else if (res.status === 401) {
                // httpClient already called clearAuthCache(); reflect that here too.
                setInstalledAppIds([]);
            }
            // other non-ok statuses: retain existing data (network hiccup etc.)
        } catch {
            // retain existing data on network error
        } finally {
            setIsLoaded(true);
        }
    }, []);

    // Called on every identity update (e.g. after login or token refresh).
    // Re-fetches the authoritative installed-app list so that TenantAppSetting changes
    // made while the user was logged in are reflected without a hard reload.
    const syncFromCache = React.useCallback(() => {
        setIdentity(getIdentityCached());
        void reload();
    }, [reload]);

    React.useEffect(() => {
        void reload();
        window.addEventListener('fbr-identity-changed', syncFromCache);
        return () => window.removeEventListener('fbr-identity-changed', syncFromCache);
    }, [reload, syncFromCache]);

    /**
     * Call whenever the user explicitly opens an app (OdooAppSwitcher, SelectApp, etc.).
     * 1. Updates lastUsedAppId in localStorage immediately for instant effect on page reload.
     * 2. Fires a background PATCH to persist it server-side.
     */
    const persistLastUsedApp = React.useCallback((appId: string) => {
        const current = getIdentityCached();
        if (current) {
            const updated = { ...current, lastUsedAppId: appId };
            setIdentityCached(updated);
            setIdentity(updated);
        }
        if (getAccessToken()) {
            void apiFetch('/api/user/preferences/last-used-app', {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ appId }),
            });
        }
    }, []);

    const defaultStartupAppId = React.useMemo(
        () => resolveStartupApp(identity, installedAppIds, APPS_REGISTRY)?.id,
        [identity, installedAppIds]
    );

    const value = React.useMemo<TenantAppsContextValue>(
        () => ({
            installedAppIds,
            isLoaded,
            isAppInstalled: appId => isAppInstalledIn(installedAppIds, appId),
            getInstalledApps: () => getInstalledApps(APPS_REGISTRY, installedAppIds),
            defaultStartupAppId,
            persistLastUsedApp,
            reload,
        }),
        [installedAppIds, isLoaded, defaultStartupAppId, persistLastUsedApp, reload]
    );

    return <TenantAppsContext.Provider value={value}>{children}</TenantAppsContext.Provider>;
}
